import { ContentCopy, DoneAll } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, styled } from '@mui/material';
import { useState } from 'react';
import { FailureSubjectKind, useGetAssetFailureDiagnostics } from '@/hooks/api/assets/useGetAssetFailureDiagnostics';

const TooltipBody = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
});

const NO_TRACE_ID = 'No trace ID found for this failure';

export const TraceIdCell = ({ assetId, kind }: { assetId: string; kind?: FailureSubjectKind }) => {
  // Nothing is looked up until the row is actually pointed at: a column that resolved on
  // render would run one CloudWatch query per visible row.
  const [isRequested, setIsRequested] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { data, isLoading, isError } = useGetAssetFailureDiagnostics({ assetId, kind, enabled: isRequested });

  const traceId = data?.status === 'found' ? data.traceId : undefined;
  const isResolving = isRequested && isLoading;
  // Absence is only knowable after looking, so the icon stays live until a lookup comes back
  // empty. Dimming every row up front would claim no row has a trace id.
  const isKnownAbsent = isRequested && !isLoading && !traceId;

  const copy = () => {
    setIsRequested(true);
    if (!traceId) return;
    void navigator.clipboard.writeText(traceId);
    setIsCopied(true);
  };

  const tooltip = (
    <TooltipBody data-testid='trace-id-tooltip'>
      {isResolving && <CircularProgress size={12} color='inherit' />}
      {isResolving && 'Looking up trace ID'}
      {!isResolving && traceId && (isCopied ? 'Copied' : traceId)}
      {!isResolving && !traceId && (isError ? 'Could not look up the trace ID' : NO_TRACE_ID)}
    </TooltipBody>
  );

  return (
    <Tooltip title={tooltip} onOpen={() => setIsRequested(true)} onClose={() => setIsCopied(false)}>
      <span data-testid='trace-id-cell'>
        <IconButton
          size='small'
          onClick={copy}
          disabled={isKnownAbsent}
          aria-label='Copy trace ID'
          data-testid='trace-id-button'
        >
          {isCopied ? <DoneAll sx={{ fontSize: '0.875rem' }} /> : <ContentCopy sx={{ fontSize: '0.875rem' }} />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
